import { useEffect, useRef } from 'react';

import noop from './noop';
import useEventCallback from './useEventCallback';
import useThrowError from './useThrowError';

export interface BroadcastChannelOptions {
  channelName: string;
  onMessage(data: unknown): void;
  skip?: boolean;
}

export interface BroadcastChannelActions {
  broadcast(data: unknown): void;
}

export default function useBroadcastChannel({
  channelName,
  onMessage,
  skip = false,
}: BroadcastChannelOptions): BroadcastChannelActions {
  skip ||= typeof BroadcastChannel === 'undefined';
  skip ||= process.env.NODE_ENV === 'test';

  const throwError = useThrowError();
  const broadcastChannelRef = useRef<BroadcastChannel>();
  const broadcastChannelQueueRef = useRef<unknown[]>();

  const broadcast = useEventCallback((message: unknown) => {
    if (broadcastChannelRef.current != null) {
      broadcastChannelRef.current.postMessage(message);
    } else {
      (broadcastChannelQueueRef.current ??= []).push(message);
    }
  });

  const handleMessage = useEventCallback((event: MessageEvent<unknown>) => {
    onMessage(event.data);
  });

  const handleMessageError = useEventCallback(
    (event: MessageEvent<DOMException>) => {
      throwError(
        new Error('Message error in useBroadcastChannel', {
          cause: event.data,
        }),
      );
    },
  );

  useEffect(() => {
    if (skip) {
      return noop;
    }

    const broadcastChannel = new BroadcastChannel(channelName);

    broadcastChannelRef.current = broadcastChannel;
    broadcastChannel.addEventListener('message', handleMessage);
    broadcastChannel.addEventListener('messageerror', handleMessageError);

    if (broadcastChannelQueueRef.current != null) {
      for (const message of broadcastChannelQueueRef.current) {
        broadcastChannel.postMessage(message);
      }
      broadcastChannelQueueRef.current = undefined;
    }

    return () => {
      broadcastChannel.close();
      broadcastChannel.removeEventListener('message', handleMessage);
      broadcastChannel.removeEventListener('messageerror', handleMessageError);
      if (broadcastChannelRef.current === broadcastChannel) {
        broadcastChannelRef.current = undefined;
      }
    };
  }, [channelName, handleMessage, handleMessageError, skip]);

  return { broadcast };
}
